import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { api } from '~/lib/api';
import { onRealtime } from '~/lib/realtime';

export type NotificationType =
  | 'order.new'
  | 'order.shipped'
  | 'question.new'
  | 'price.updated'
  | 'system';

export interface NotificationRow {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  link?: string | null;
  read: boolean;
  createdAt: string;
  storeId?: string | null;
  meta?: Record<string, unknown> | null;
}

interface ListResponse {
  notifications: NotificationRow[];
  total: number;
  unreadCount: number;
  page: number;
  pageSize: number;
}

const PAGE_SIZE = 20;

export const useNotificationsStore = defineStore('notifications', () => {
  const items = ref<NotificationRow[]>([]);
  const unreadCount = ref(0);
  const total = ref(0);
  const loading = ref(false);
  const hydrated = ref(false);
  let unsubscribe: (() => void) | null = null;

  const hasUnread = computed(() => unreadCount.value > 0);

  async function refresh(): Promise<void> {
    loading.value = true;
    try {
      const res = await api.get<ListResponse>('/notifications', {
        params: { page: 1, pageSize: PAGE_SIZE },
      });
      items.value = res.data.notifications;
      unreadCount.value = res.data.unreadCount;
      total.value = res.data.total;
      hydrated.value = true;
    } finally {
      loading.value = false;
    }
  }

  async function refreshCount(): Promise<void> {
    const res = await api.get<{ count: number }>('/notifications/unread-count');
    unreadCount.value = res.data.count;
  }

  async function markRead(id: string): Promise<void> {
    const target = items.value.find((n) => n.id === id);
    if (target && !target.read) {
      target.read = true;
      unreadCount.value = Math.max(0, unreadCount.value - 1);
    }
    try {
      await api.post(`/notifications/${id}/read`);
    } catch {
      // Reconcile with server on failure.
      await refresh();
    }
  }

  async function markAllRead(): Promise<void> {
    const previouslyUnread = items.value.filter((n) => !n.read);
    for (const n of previouslyUnread) n.read = true;
    unreadCount.value = 0;
    try {
      await api.post('/notifications/read-all');
    } catch {
      await refresh();
    }
  }

  /** Hook the realtime stream — call once after login. Idempotent. */
  function attach(): void {
    if (unsubscribe) return;
    unsubscribe = onRealtime((e) => {
      if (e.type !== 'notification:new') return;
      const row = e.payload as NotificationRow;
      // Prepend if not already present (server could deliver twice on reconnect).
      if (!items.value.some((n) => n.id === row.id)) {
        items.value = [row, ...items.value].slice(0, PAGE_SIZE);
        if (!row.read) unreadCount.value += 1;
      }
    });
  }

  function reset(): void {
    items.value = [];
    unreadCount.value = 0;
    total.value = 0;
    hydrated.value = false;
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
  }

  return {
    items,
    unreadCount,
    total,
    loading,
    hydrated,
    hasUnread,
    refresh,
    refreshCount,
    markRead,
    markAllRead,
    attach,
    reset,
  };
});
